import React from "react";
import ReactHtmlParser from "react-html-parser";
import { renderToStaticMarkup } from "react-dom/server";
import { getFormattedUnit } from "config";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const uppercaseMeridiem = (value) =>
  value.replace(/\b(am|pm)\b/gi, (meridiem) => meridiem.toUpperCase());

const formatAmount = (value) =>
  `Rs. ${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numberValue(value))}`;

const formatDate = (value, withTime = false) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return uppercaseMeridiem(
    date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...(withTime
        ? { hour: "2-digit", minute: "2-digit", hour12: true }
        : {}),
    })
  );
};

const getSubmissionDateParts = (value) => {
  if (!value) return { dayDate: "-", time: "-" };

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { dayDate: "-", time: "-" };

  return {
    dayDate: date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: uppercaseMeridiem(
      date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    ),
  };
};

const escapeHtml = (value) =>
  String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const displayValue = (value) =>
  escapeHtml(value === null || value === undefined || value === "" ? "-" : value);

const richTextValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value !== "string") return displayValue(value);

  const parsedValue = ReactHtmlParser(value);
  return renderToStaticMarkup(
    React.createElement(React.Fragment, null, parsedValue)
  );
};

const getProjectName = (rfqData) =>
  String(rfqData?.projectName || "-")
    .split("||")
    .map((value) => value.trim())
    .filter(Boolean)
    .join(", ") || "-";

const getDeliveryLocation = (rfqData) =>
  String(rfqData?.location || "-")
    .split("||")
    .map((value) => value.trim())
    .filter(Boolean)
    .join(", ") || "-";

const getSubmittedBy = (quotationSet) => quotationSet?.submittedBy || "-";

const getDisplayRfqStatus = (status) => {
  const normalizedStatus = String(status || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toUpperCase();

  if (normalizedStatus === "PENDING APPROVAL") return "In Review";
  if (!normalizedStatus) return "-";

  return normalizedStatus.charAt(0) + normalizedStatus.slice(1).toLowerCase();
};

const getQuotationAmount = (quotationSet) => {
  const amountWithoutGst =
    numberValue(quotationSet?.totalWithoutGst) +
    numberValue(quotationSet?.othersTotalWithoutGst);

  if (amountWithoutGst > 0) return amountWithoutGst;

  const amountWithGst =
    numberValue(quotationSet?.totalWithGst) +
    numberValue(quotationSet?.othersTotalWithGst);

  if (amountWithGst > 0) return amountWithGst;

  return numberValue(quotationSet?.selectedTotalWithGst);
};

const formatQuotationDifference = (difference, previousAmount) => {
  if (!difference) return "--";

  // const sign = difference > 0 ? "+" : "-";
  const percentage =
    previousAmount > 0
      ? ` (${((Math.abs(difference) / previousAmount) * 100).toFixed(2)}%)`
      : "";

  return ` ${formatAmount(Math.abs(difference))}${percentage}`;
};

const getRfqReceivedOn = (rfqData) =>
  rfqData?.receivedAt || rfqData?.rfqReceivedAt || rfqData?.createdAt;

const normalizeMaterialName = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .trim()
    .toLowerCase();

const getRfqMaterial = (item, rfqData, index) => {
  const itemId = item?.id ?? item?._id;
  const itemCode = item?.code;
  const itemName = normalizeMaterialName(item?.name);

  const matchedMaterial = (rfqData?.materials || []).find((material) => {
    const sameId =
      itemId !== undefined &&
      itemId !== null &&
      String(material?._id ?? material?.id) === String(itemId);
    const sameCode =
      itemCode && material?.code && String(material.code) === String(itemCode);
    const sameName =
      itemName && normalizeMaterialName(material?.name) === itemName;

    return sameId || sameCode || sameName;
  });

  return matchedMaterial || rfqData?.materials?.[index];
};

const getMaterialRows = (rfqData, quotationSet) =>
  (quotationSet?.materialDetails || []).map((item, index) => {
    const rfqMaterial = getRfqMaterial(item, rfqData, index);
    const quantity = numberValue(item?.quantity);
    const lineCost = numberValue(item?.priceWithoutGst);
    const itemRate = quantity > 0 ? lineCost / quantity : item?.basePrice;
    const mrp = item?.basePrice ?? rfqMaterial?.basePrice ?? itemRate;
    const materialQuantity = rfqMaterial?.quantity ?? item?.quantity;
    const materialUnit = item?.unit || rfqMaterial?.unit;
    const formattedUnit = getFormattedUnit(materialUnit);

    return {
      number: index + 1,
      name: item?.name,
      quantity: `${materialQuantity ?? "-"}${formattedUnit ? ` ${formattedUnit}` : ""}`,
      brand: item?.brand,
      rate: formatAmount(mrp),
      discount: `${item?.discount ?? 0}%`,
      gst: `${item?.gst ?? 0}%`,
      itemRate: formatAmount(itemRate),
      cost: formatAmount(lineCost),
      remarks: item?.remark,
    };
  });

const normalizeLabourSkill = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .trim()
    .toLowerCase();

const getRfqLabour = (item, rfqData, index) => {
  const itemId = item?.id ?? item?._id;
  const itemSkill = normalizeLabourSkill(item?.skill);

  const matchedLabour = (rfqData?.labourDetails || []).find((labour) => {
    const sameId =
      itemId !== undefined &&
      itemId !== null &&
      String(labour?._id ?? labour?.id) === String(itemId);
    const sameSkill =
      itemSkill && normalizeLabourSkill(labour?.skill) === itemSkill;

    return sameId || sameSkill;
  });

  return matchedLabour || rfqData?.labourDetails?.[index];
};

const formatBrands = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "-";
  return value || "-";
};

const formatQuantityUnit = (quantity, unit) => {
  const formattedUnit = getFormattedUnit(unit);
  return `${quantity ?? "-"}${formattedUnit ? ` ${formattedUnit}` : ""}`;
};

const hasTextValue = (value) =>
  String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim() !== "";

const getLabourRows = (rfqData, quotationSet) =>
  (quotationSet?.labourItems || []).map((item, index) => {
    const rfqLabour = getRfqLabour(item, rfqData, index);
    const labourUnit = item?.unit || rfqLabour?.unit;
    const labourBrands =
      item?.brands || item?.brand || rfqLabour?.brands || rfqLabour?.brand;

    return {
      number: index + 1,
      name: item?.skill,
      quantity: `${item?.quantity ?? rfqLabour?.quantity ?? "-"}${
        getFormattedUnit(labourUnit) ? ` ${getFormattedUnit(labourUnit)}` : ""
      }`,
      brand: formatBrands(labourBrands),
      rate: formatAmount(item?.labourRate),
      discount: "-",
      gst: `${item?.gst ?? 0}%`,
      itemRate: formatAmount(item?.labourRate),
      cost: formatAmount(item?.priceWithoutGst),
      remarks: item?.remarks,
    };
  });

const getContractorSections = (quotationSet) =>
  (quotationSet?.contractorDetails || []).map((section, sectionIndex) => ({
    ...section,
    sectionNumber: sectionIndex + 1,
    scopes: (section?.scopes || []).map((scope, scopeIndex) => ({
      ...scope,
      scopeNumber: `${sectionIndex + 1}.${scopeIndex + 1}`,
      materials: (scope?.materials || []).map((material, materialIndex) => ({
        ...material,
        materialNumber: `${sectionIndex + 1}.${scopeIndex + 1}.${materialIndex + 1}`,
      })),
    })),
  }));

const getContractorRows = (quotationSet) => {
  const rows = [];

  getContractorSections(quotationSet).forEach((section) => {
    section.scopes.forEach((scope) => {
      const materials = scope?.materials || [];

      if (materials.length === 0) {
        rows.push({
          number: rows.length + 1,
          name: scope?.scopeOfWork || "Scope",
          quantity: formatQuantityUnit(scope?.quantity, scope?.unit),
          brand: formatBrands(scope?.preferredBrands),
          rate: formatAmount(scope?.basePrice),
          discount: "-",
          gst: `${scope?.gst ?? 0}%`,
          itemRate: formatAmount(scope?.basePrice),
          cost: formatAmount(scope?.totalExclGst),
          remarks: scope?.comments,
        });
        return;
      }

      materials.forEach((item) => {
        rows.push({
          number: rows.length + 1,
          name: `${scope?.scopeOfWork || "Scope"} - ${item?.name || "Material"}`,
          quantity: formatQuantityUnit(
            item?.quantity ?? scope?.quantity,
            item?.unit || scope?.unit
          ),
          brand: formatBrands(item?.brands || scope?.preferredBrands),
          rate: formatAmount(item?.basePrice),
          discount: `${item?.discount ?? 0}%`,
          gst: `${item?.gst ?? 0}%`,
          itemRate: formatAmount(item?.basePrice),
          cost: formatAmount(item?.totalExclGst),
          remarks: item?.remarks || scope?.comments,
        });
      });
    });
  });

  return rows;
};

const getRows = (rfqData, quotationSet) => {
  if (rfqData?.rfqType === "labour") return getLabourRows(rfqData, quotationSet);
  if (rfqData?.rfqType === "contractor") return getContractorRows(quotationSet);
  return getMaterialRows(rfqData, quotationSet);
};

const visibleChargeKeysByType = {
  material: ["transport", "loading", "unLoading", "mathadeCharges", "miscCharges"],
  contractor: ["transport", "loading", "unLoading", "mathadeCharges", "miscCharges"],
  labour: ["mathadeCharges", "miscCharges"],
};

const chargeLabels = {
  transport: "Transport",
  loading: "Loading",
  unLoading: "Unloading",
  mathadeCharges: "Mathade Charges",
  miscCharges: "Miscellaneous Charges",
};

const getChargeRows = (rfqData, quotationSet) => {
  const visibleChargeKeys = visibleChargeKeysByType[rfqData?.rfqType] || [];
  const rfqOtherCharges = rfqData?.otherCharges || {};

  return Object.entries(quotationSet?.otherCharges || {})
    .filter(([name]) => visibleChargeKeys.includes(name) && rfqOtherCharges?.[name])
    .map(([name, charge = {}]) => ({
      name: chargeLabels[name] || name,
      rateValue: numberValue(charge?.mrp),
      rate: formatAmount(charge?.mrp),
      gst: `${charge?.gst ?? 0}%`,
      remarks: charge?.remark,
      totalValue: numberValue(charge?.totalCost),
      total: formatAmount(charge?.totalCost),
    }));
};

const getHtml = ({
  rfqData,
  quotationSet,
  quotationIndex,
  quotationHistory = [],
}) => {
  const rows = getRows(rfqData, quotationSet);
  const isContractor = rfqData?.rfqType === "contractor";
  const isLabour = rfqData?.rfqType === "labour";
  const contractorSections = isContractor
    ? getContractorSections(quotationSet)
    : [];
  const chargeRows = getChargeRows(rfqData, quotationSet);
  const previousQuotationHistory = quotationHistory.filter(
    ({ quotationIndex: historyIndex }) =>
      Number(historyIndex) < Number(quotationIndex)
  );
  const visibleOtherChargesWithoutGst = chargeRows.reduce(
    (total, charge) => total + charge.rateValue,
    0
  );
  const visibleOtherChargesWithGst = chargeRows.reduce(
    (total, charge) => total + charge.totalValue,
    0
  );
  const basicAmount =
    numberValue(quotationSet?.totalWithoutGst) + visibleOtherChargesWithoutGst;
  const quotationValue =
    numberValue(quotationSet?.totalWithGst) + visibleOtherChargesWithGst;
  const gstTotal = quotationValue - basicAmount;
  const submittedBy = getSubmittedBy(quotationSet);
  const revision = `R${quotationIndex}`;
  const fileTitle = `Quotation-${rfqData?.name || "RFQ"}-${revision}`
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  const tableTitle =
    rfqData?.rfqType === "labour"
      ? "LABOUR LIST"
      : rfqData?.rfqType === "contractor"
        ? "WORK SCOPE LIST"
        : "MATERIAL LIST";

  const infoCell = (label, value) => `
    <div class="info-cell">
      <div class="label">${escapeHtml(label)}</div>
      <div class="value">${displayValue(value)}</div>
    </div>`;

  const itemRows = rows.length
    ? rows
        .map((row) => {
          const itemCommentRow = hasTextValue(row.remarks)
            ? `<tr class="line-comment-row"><td colspan="${isLabour ? 7 : 9}"><span class="line-comment-label">Remarks:</span> <span class="rich-text">${richTextValue(row.remarks)}</span></td></tr>`
            : "";

          return `
          <tr>
            <td>${row.number}</td>
            <td class="strong rich-text">${richTextValue(row.name)}</td>
            <td>${displayValue(row.quantity)}</td>
            ${isLabour ? "" : `<td class="rich-text">${richTextValue(row.brand)}</td>`}
            <td class="right">${row.rate}</td>
            ${isLabour ? "" : `<td class="center">${displayValue(row.discount)}</td>`}
            <td class="center">${displayValue(row.gst)}</td>
            <td class="right">${row.itemRate}</td>
            <td class="right strong">${row.cost}</td>
          </tr>${itemCommentRow}`;
        })
        .join("")
    : `<tr><td colspan="${isLabour ? 7 : 9}" class="empty">No quotation line items provided</td></tr>`;

  const itemTableHeader = isLabour
    ? `<thead><tr><th class="center">Sr #</th><th>Item</th><th>Qty / Unit</th><th class="right">Rate</th><th class="center">GST</th><th class="right">Item Rate</th><th class="right">Amount</th></tr></thead>`
    : `<thead><tr><th class="center">Sr #</th><th>Item</th><th>Qty / Unit</th><th>Brand</th><th class="right">MRP</th><th class="center">Disc</th><th class="center">GST</th><th class="right">Item Rate</th><th class="right">Amount</th></tr></thead>`;

  const contractorTableHeader = `
    <thead>
      <tr>
        <th class="center">Ref</th>
        <th>Description</th>
        <th>Brands</th>
        <th>Qty / Unit</th>
        <th class="right">Material<br />Rate</th>
        <th class="right">Installation<br />Rate</th>
        <th class="center">GST %</th>
        <th class="right">Supply<br />Cost</th>
        <th class="right">Installation<br />Cost</th>
        <th class="right">Total<br />(Excl. GST)</th>
      </tr>
    </thead>`;

  const contractorSectionsMarkup = contractorSections.length
    ? contractorSections
        .map((section) => {
          const sectionTotal =
            section?.sectionTotalExclGst ??
            section.scopes.reduce(
              (total, scope) => total + numberValue(scope?.totalExclGst),
              0
            );

          const sectionRows = section.scopes
            .map((scope) => {
              const materials = scope?.materials || [];
              const scopeQuantity = formatQuantityUnit(scope?.quantity, scope?.unit);
              const scopeBrands = formatBrands(scope?.preferredBrands);
              const hasScopeQuantity =
                Boolean(scope?.unit) ||
                (scope?.quantity !== undefined &&
                  scope?.quantity !== null &&
                  String(scope.quantity).trim() !== "" &&
                  String(scope.quantity).trim() !== "0");
              const hasScopeBrands = scopeBrands !== "-";
              const scopeTableRow = materials.length
                ? `
                    <tr>
                      <td class="center strong">SoW ${displayValue(scope.scopeNumber)}</td>
                      <td class="strong rich-text">${richTextValue(scope?.scopeOfWork || "Scope")}</td>
                      <td class="rich-text">${hasScopeBrands ? richTextValue(scopeBrands) : ""}</td>
                      <td>${hasScopeQuantity ? displayValue(scopeQuantity) : ""}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>`
                : `
                    <tr>
                      <td class="center strong">SoW ${displayValue(scope.scopeNumber)}</td>
                      <td class="strong rich-text">${richTextValue(scope?.scopeOfWork || "Scope")}</td>
                      <td class="rich-text">${richTextValue(scopeBrands)}</td>
                      <td>${displayValue(scopeQuantity)}</td>
                      <td class="right">${formatAmount(scope?.basePrice)}</td>
                      <td class="right">${formatAmount(scope?.installationRate)}</td>
                      <td class="center">${displayValue(`${scope?.gst ?? 0}%`)}</td>
                      <td class="right">${formatAmount(scope?.supplyCost)}</td>
                      <td class="right">${formatAmount(scope?.installationCost)}</td>
                      <td class="right strong">${formatAmount(scope?.totalExclGst)}</td>
                    </tr>`;
              const scopeCommentRow = hasTextValue(scope?.comments)
                ? `<tr class="line-comment-row"><td colspan="10"><span class="line-comment-label">Comments / Methodology:</span> <span class="rich-text">${richTextValue(scope.comments)}</span></td></tr>`
                : "";
              const materialRows = materials
                .map((material) => {
                  const materialCommentRow = hasTextValue(material?.remarks)
                    ? `<tr class="line-comment-row"><td colspan="10"><span class="line-comment-label">Remarks:</span> <span class="rich-text">${richTextValue(material.remarks)}</span></td></tr>`
                    : "";

                  return `
                    <tr class="material-row">
                      <td class="center strong">M ${displayValue(material.materialNumber)}</td>
                      <td class="strong rich-text">${richTextValue(material?.name || "Material")}</td>
                      <td class="rich-text">${richTextValue(formatBrands(material?.brands))}</td>
                      <td>${displayValue(formatQuantityUnit(material?.quantity ?? scope?.quantity, material?.unit || scope?.unit))}</td>
                      <td class="right">${formatAmount(material?.basePrice)}</td>
                      <td class="right">${formatAmount(material?.installationRate)}</td>
                      <td class="center">${displayValue(`${material?.gst ?? 0}%`)}</td>
                      <td class="right">${formatAmount(material?.netPrice)}</td>
                      <td class="right">${formatAmount(material?.installationCost)}</td>
                      <td class="right strong">${formatAmount(material?.totalExclGst)}</td>
                    </tr>${materialCommentRow}`;
                })
                .join("");

              const materialGroupRow = materials.length
                ? `<tr class="material-group-row"><td colspan="10">Materials for Scope ${displayValue(scope.scopeNumber)} <span>(${materials.length} ${materials.length === 1 ? "item" : "items"})</span></td></tr>`
                : "";

              return `${scopeTableRow}${scopeCommentRow}${materialGroupRow}${materialRows}`;
            })
            .join("");

          return `
            <section class="contractor-section">
              <div class="contractor-section-header">
                <div><span class="section-number">Section ${displayValue(section.sectionNumber)}</span><strong>${richTextValue(section?.sectionName || "Contractor Work")}</strong></div>
                <div>Section Total (Excl. GST): <strong>${formatAmount(sectionTotal)}</strong></div>
              </div>
              <table class="contractor-table section-table">
                ${contractorTableHeader}
                <tbody>${sectionRows}</tbody>
              </table>
            </section>`;
        })
        .join("")
    : `<div class="empty">No contractor work scope details provided</div>`;

  const quotationListMarkup = isContractor
    ? `<div class="contractor-list">${contractorSectionsMarkup}</div>`
    : `
      <table class="items${isLabour ? " labour-items" : " material-items"}">
        ${itemTableHeader}
        <tbody>${itemRows}</tbody>
      </table>`;

  const quotationHistoryRows = previousQuotationHistory
    .map(({ quotationSet: historySet, quotationIndex: historyIndex }, index) => {
      const submittedBy = getSubmittedBy(historySet);
      const submittedDateParts = getSubmissionDateParts(historySet?.submittedAt);
      const quotationAmount = getQuotationAmount(historySet);
      const previousAmount =
        index > 0
          ? getQuotationAmount(previousQuotationHistory[index - 1].quotationSet)
          : 0;
      const difference = index > 0 ? quotationAmount - previousAmount : null;
      const revisionLabel =
        historyIndex === 0
          ? "submitted R0 Quote"
          : `R${historyIndex}(updated quote)`;

      return `
        <tr>
          <td>
            <div class="history-person">
              <div>
                <strong>${displayValue(submittedBy)}</strong>
                <span>${displayValue(revisionLabel)}</span>
              </div>
            </div>
          </td>
          <td>${displayValue(submittedDateParts.dayDate)}</td>
          <td>${displayValue(submittedDateParts.time)}</td>
          <td class="right">${index > 0 ? formatQuotationDifference(difference, previousAmount) : "--"}</td>
          <td class="right strong">${formatAmount(quotationAmount)}</td>
        </tr>`;
    })
    .join("");

  const quotationHistorySection = previousQuotationHistory.length
    ? `
      <section class="quotation-history">
        <h2 class="section-title">QUOTATION UPDATION TRAIL</h2>
        <p class="history-description">Previous quotation versions submitted before this revision.</p>
        <table class="history-table">
          <thead><tr><th>Submitted By</th><th>Day &amp; Date</th><th>Time</th><th class="right">Difference</th><th class="right">Quotation Amount</th></tr></thead>
          <tbody>${quotationHistoryRows}</tbody>
        </table>
      </section>`
    : "";

  const additionalRows = chargeRows.length
    ? chargeRows
        .map(
          (charge) => `
          <tr>
            <td>${displayValue(charge.name)}</td>
            <td class="right">${charge.rate}</td>
            <td class="center">${displayValue(charge.gst)}</td>
            <td class="rich-text">${richTextValue(charge.remarks)}</td>
            <td class="right strong">${charge.total}</td>
          </tr>`
        )
        .join("")
    : `<tr><td colspan="5" class="empty">No additional charges</td></tr>`;

  const additionalCostSection = chargeRows.length
    ? `
      <h2 class="section-title">ADDITIONAL COST COMPONENTS</h2>
      <table class="additional">
        <thead><tr><th>Charge</th><th class="right">Rate</th><th class="center">GST %</th><th>Remarks</th><th class="right">Total</th></tr></thead>
        <tbody>${additionalRows}</tbody>
      </table>`
    : "";

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(fileTitle)}</title>
    <style>
      @page { size: A4; margin: 12mm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      body { margin: 0; color: #0b0e14; font-family: Arial, Helvetica, sans-serif; font-size: 9px; }
      .page { width: 100%; max-width: 190mm; margin: 0 auto; }
      .contractor-page { max-width: 190mm; }
      .top { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 9px; border-bottom: 1.5px solid #e45517; }
      .brand { font-size: 15px; font-weight: 700; }
      .title { font-size: 18px; font-weight: 700; letter-spacing: .4px; }
      .subhead { display: flex; justify-content: space-between; margin: 12px 0 11px; color: #6b6e76; }
      .subhead strong { color: #0b0e14; font-size: 10px; }
      .revision { color: #e45517; font-weight: 700; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #deddda; }
      .info-cell { min-height: 44px; padding: 7px 6px 6px; border-bottom: 1px solid #deddda; }
      .info-cell:nth-child(odd) { border-right: 1px solid #deddda; }
      .label { color: #6b6e76; font-size: 7.5px; text-transform: uppercase; letter-spacing: .15px; margin-bottom: 7px; }
      .value { color: #0b0e14; font-size: 9.3px; font-weight: 700; overflow-wrap: anywhere; }
      .section-title { margin: 14px 0 6px; font-size: 10.5px; font-weight: 700; }
      .quotation-history { margin-top: 14px; page-break-inside: avoid; }
      .history-description { margin: 0 0 5px; color: #6b6e76; font-size: 7.5px; }
      .history-table { width: 100%; border: 1px solid #deddda; table-layout: fixed; font-size: 7.5px; }
      .history-table th { padding: 6px 5px; background: #f6f6f4; color: #6b6e76; border-bottom: 1px solid #deddda; font-size: 7px; }
      .history-table td { padding: 6px 5px; border-bottom: 1px solid #deddda; vertical-align: middle; }
      .history-table tbody tr:last-child td { border-bottom: 0; }
      .history-table th:nth-child(1), .history-table td:nth-child(1) { width: 28%; }
      .history-table th:nth-child(2), .history-table td:nth-child(2) { width: 22%; }
      .history-table th:nth-child(3), .history-table td:nth-child(3) { width: 16%; }
      .history-table th:nth-child(4), .history-table td:nth-child(4) { width: 17%; }
      .history-table th:nth-child(5), .history-table td:nth-child(5) { width: 17%; }
      .history-person { display: flex; align-items: center; gap: 6px; min-width: 0; }
      .history-avatar { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; width: 17px; height: 17px; border-radius: 50%; background: #e4e7ff; color: #1f2a44; font-size: 7px; font-weight: 700; }
      .history-person > div { min-width: 0; }
      .history-person strong, .history-person span { display: block; overflow-wrap: anywhere; }
      .history-person span { color: #6b6e76; font-size: 6.8px; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      th { padding: 6px 3px; background: #0b0e14; color: #fff; text-align: left; font-size: 7px; line-height: 1.15; vertical-align: middle; }
      td { padding: 5px 3px; border-bottom: 1px solid #deddda; color: #0b0e14; vertical-align: top; overflow-wrap: anywhere; line-height: 1.25; }
      tbody tr:nth-child(even) td { background: #fff; }
      .items { font-size: 9px; }
      .items th { font-size: 8px; }
      .material-items th:nth-child(1), .material-items td:nth-child(1) { width: 4%; }
      .material-items th:nth-child(2), .material-items td:nth-child(2) { width: 18%; }
      .material-items th:nth-child(3), .material-items td:nth-child(3) { width: 10%; }
      .material-items th:nth-child(4), .material-items td:nth-child(4) { width: 14%; }
      .material-items th:nth-child(5), .material-items td:nth-child(5) { width: 12%; }
      .material-items th:nth-child(6), .material-items td:nth-child(6) { width: 7%; }
      .material-items th:nth-child(7), .material-items td:nth-child(7) { width: 7%; }
      .material-items th:nth-child(8), .material-items td:nth-child(8) { width: 14%; }
      .material-items th:nth-child(9), .material-items td:nth-child(9) { width: 14%; }
      .labour-items th:nth-child(1), .labour-items td:nth-child(1) { width: 4%; }
      .labour-items th:nth-child(2), .labour-items td:nth-child(2) { width: 27%; }
      .labour-items th:nth-child(3), .labour-items td:nth-child(3) { width: 14%; }
      .labour-items th:nth-child(4), .labour-items td:nth-child(4) { width: 16%; }
      .labour-items th:nth-child(5), .labour-items td:nth-child(5) { width: 9%; }
      .labour-items th:nth-child(6), .labour-items td:nth-child(6) { width: 16%; }
      .labour-items th:nth-child(7), .labour-items td:nth-child(7) { width: 14%; }
      .additional th:nth-child(1), .additional td:nth-child(1) { width: 24%; }
      .additional th:nth-child(2), .additional td:nth-child(2) { width: 18%; }
      .additional th:nth-child(3), .additional td:nth-child(3) { width: 14%; }
      .additional th:nth-child(4), .additional td:nth-child(4) { width: 28%; }
      .additional th:nth-child(5), .additional td:nth-child(5) { width: 16%; }
      .contractor-section { margin-bottom: 14px; page-break-inside: avoid; }
      .contractor-section-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 8px 9px; border: 1px solid #deddda; border-left: 4px solid #e45517; background: #fff0e4; font-size: 8px; }
      .contractor-section-header > div { min-width: 0; overflow-wrap: anywhere; }
      .contractor-section-header strong { font-size: 9px; }
      .section-number { margin-right: 8px; color: #e45517; font-weight: 700; text-transform: uppercase; }
      .section-table { page-break-inside: avoid; }
      .material-group-row td { padding: 6px 8px 4px; background: #f6f6f4 !important; color: #6b6e76; font-size: 7.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .15px; }
      .material-group-row span { font-weight: 400; text-transform: none; }
      .material-row td:nth-child(2) { padding-left: 10px; }
      .line-comment-row td { padding: 3px 8px 6px; background: #fff !important; color: #0b0e14; font-size: inherit; }
      .line-comment-label { color: #0b0e14; font-weight: 700; }
      .contractor-table { width: 100%; table-layout: fixed; font-size: 7.2px; }
      .contractor-table th { padding: 5px 3px; font-size: 6.5px; }
      .contractor-table td { padding: 5px 3px; }
      .contractor-table th:nth-child(1), .contractor-table td:nth-child(1) { width: 5%; }
      .contractor-table th:nth-child(2), .contractor-table td:nth-child(2) { width: 22%; }
      .contractor-table th:nth-child(3), .contractor-table td:nth-child(3) { width: 13%; }
      .contractor-table th:nth-child(4), .contractor-table td:nth-child(4) { width: 10%; }
      .contractor-table th:nth-child(5), .contractor-table td:nth-child(5) { width: 9%; }
      .contractor-table th:nth-child(6), .contractor-table td:nth-child(6) { width: 10%; }
      .contractor-table th:nth-child(7), .contractor-table td:nth-child(7) { width: 6%; }
      .contractor-table th:nth-child(8), .contractor-table td:nth-child(8) { width: 8%; }
      .contractor-table th:nth-child(9), .contractor-table td:nth-child(9) { width: 8%; }
      .contractor-table th:nth-child(10), .contractor-table td:nth-child(10) { width: 9%; }
      .contractor-table .rich-text p { margin: 0; }
      .strong { font-weight: 700; }
      .right { text-align: right; }
      .center { text-align: center; }
      .rich-text p { margin: 0; }
      .rich-text ul, .rich-text ol { margin: 0 0 0 12px; padding: 0; }
      .rich-text li { margin: 0; }
      .empty { text-align: center; color: #6b6e76; }
      .rfq-id { color: #6b6e76; font-size: 8px; font-weight: 700; }
      .subtotal { display: grid; grid-template-columns: 4% 16% 9% 13% 11% 6% 6% 11% 12% 12%; padding: 9px 3px; margin-top: 0; border-bottom: 1px solid #0b0e14; font-weight: 700; }
      .subtotal-label { grid-column: 1 / 9; padding-left: 3px; }
      .subtotal-value { grid-column: 9; padding-right: 3px; text-align: right; white-space: nowrap; }
      .contractor-subtotal { grid-template-columns: 5% 22% 13% 10% 9% 10% 6% 8% 8% 9%; }
      .contractor-subtotal .subtotal-label { grid-column: 1 / 10; }
      .contractor-subtotal .subtotal-value { grid-column: 10; }
      .material-subtotal { grid-template-columns: 4% 18% 10% 14% 12% 7% 7% 14% 14%; }
      .material-subtotal .subtotal-label { grid-column: 1 / 9; }
      .material-subtotal .subtotal-value { grid-column: 9; }
      .labour-subtotal { grid-template-columns: 4% 27% 14% 16% 9% 16% 14%; }
      .labour-subtotal .subtotal-label { grid-column: 1 / 7; }
      .labour-subtotal .subtotal-value { grid-column: 7; }
      .totals { margin-top: 15px; background: #fff0e4; border: 1px solid #deddda; }
      .total-row { display: flex; justify-content: space-between; padding: 7px 8px; border-bottom: 1px solid #deddda; }
      .total-row:last-child { border-bottom: 0; color: #e45517; font-size: 11.5px; font-weight: 700; }
      .terms { margin-top: 13px; padding: 9px 10px; border: 1px solid #1a8a54; background: #eaf8ef; }
      .note { margin-top: 13px; padding: 10px; border: 1px solid #deddda; background: #f6f6f4; line-height: 1.4; }
      .footer-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; margin-top: 15px; padding: 15px 6px 8px; border-top: 1px solid #deddda; color: #6b6e76; }
      .footer-grid > div { min-width: 0; overflow-wrap: anywhere; }
      .footer-grid strong { color: #0b0e14; }
      .submission-meta { margin-top: 15px; padding: 10px 6px 0; border-top: 1px solid #deddda; color: #6b6e76; text-align: right; overflow-wrap: anywhere; }
      .submission-meta strong { color: #0b0e14; }
      .footer-note { margin-top: 9px; padding-top: 8px; border-top: .5px solid #deddda; color: #6b6e76; font-size: 7px; }
      @media print { .page { max-width: none; } }
    </style>
  </head>
  <body>
    <main class="page${isContractor ? " contractor-page" : ""}">
      <header class="top"><div class="brand">Neevay</div><div class="title">QUOTATION</div></header>
      <div class="subhead">
        <div>RFQ: <strong>${displayValue(rfqData?.name)}</strong> <span class="rfq-id">(RFQ ID: ${displayValue(rfqData?.rfqId)})</span></div>
        <div>Revision <span class="revision">${revision}</span></div>
      </div>
      <section class="info-grid">
        ${infoCell("Client Name", rfqData?.userBusinessName)}
        ${infoCell("RFQ Type", rfqData?.rfqType ? rfqData.rfqType.charAt(0).toUpperCase() + rfqData.rfqType.slice(1) : "-")}
        ${infoCell("Project", getProjectName(rfqData))}
        ${infoCell("RFQ Status", getDisplayRfqStatus(rfqData?.status))}
        ${infoCell("Delivery Location", getDeliveryLocation(rfqData))}
        ${infoCell("RFQ Received On", formatDate(getRfqReceivedOn(rfqData), true))}
        ${infoCell("Buyer Contact", `${rfqData?.requesterName || "-"} | ${rfqData?.requesterMobile ? `+91-${rfqData.requesterMobile}` : "-"}`)}
        ${infoCell("Submitted On", `${submittedBy} | ${formatDate(quotationSet?.submittedAt, true)}`)}
      </section>
      ${quotationHistorySection}
      <h2 class="section-title">${tableTitle} (${rows.length} ${rows.length === 1 ? "item" : "items"})</h2>
      ${quotationListMarkup}
      <div class="subtotal${isContractor ? " contractor-subtotal" : isLabour ? " labour-subtotal" : " material-subtotal"}"><span class="subtotal-label">Sub Total</span><span class="subtotal-value">${formatAmount(quotationSet?.totalWithoutGst)}</span></div>
      ${additionalCostSection}
      <section class="totals">
        <div class="total-row"><span>Total Basic Amount (Sub Total + Other Charges)</span><strong>${formatAmount(basicAmount)}</strong></div>
        <div class="total-row"><span>GST Total</span><strong>${formatAmount(gstTotal)}</strong></div>
        <div class="total-row"><span>Total Quotation Value (incl. GST)</span><strong>${formatAmount(quotationValue)}</strong></div>
      </section>
      <div class="terms"><strong>${quotationSet?.agreedToPaymentTerms === false ? "Vendor has proposed different payment terms" : "Vendor has agreed to the buyer's payment terms"}</strong>${quotationSet?.counterPaymentTerms ? ` — <span class="rich-text">${richTextValue(quotationSet.counterPaymentTerms)}</span>` : ""}</div>
      <div class="footer-grid">
        <div><div class="label">Brand / Remarks for Client</div><strong class="rich-text">${rows.map((row) => row.brand).filter((brand) => brand !== "-").join(", ") || richTextValue(quotationSet?.notes)}</strong></div>
        <div><div class="label">Delivery Date</div><strong>${formatDate(quotationSet?.deliveryDate || rfqData?.providingDate)}</strong></div>
      </div>
      <div class="submission-meta">Submitted by: <strong>${displayValue(submittedBy)}</strong><br />on <strong>${formatDate(quotationSet?.submittedAt, true)}</strong></div>
      <div class="footer-note">This is a system-generated quotation exported from the Neevay platform. For queries regarding this RFQ, contact the buyer via the contact details above.</div>
    </main>
  </body>
</html>`;
};

const QuotationTrailPdfButton = ({
  rfqData,
  quotationSet,
  quotationIndex,
  quotationHistory,
  action = "download",
}) => {
  const getQuotationHtml = () =>
    getHtml({
      rfqData,
      quotationSet,
      quotationIndex,
      quotationHistory,
    });

  const getQuotationFileName = () => {
    const fileName = `Quotation-${rfqData?.name || "RFQ"}-R${quotationIndex}`
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "");

    return `${fileName || "Quotation"}.pdf`;
  };

  const generateQuotationPdfBlob = () =>
    new Promise((resolve, reject) => {
      const renderFrame = document.createElement("iframe");
      renderFrame.style.position = "fixed";
      renderFrame.style.left = "-10000px";
      renderFrame.style.top = "0";
      renderFrame.style.width = "794px";
      renderFrame.style.height = "1123px";
      renderFrame.style.border = "0";

      const cleanup = () => renderFrame.remove();

      renderFrame.onload = async () => {
        try {
          const quotationPage = renderFrame.contentDocument.querySelector(".page") ||
            renderFrame.contentDocument.body;
          const canvas = await html2canvas(quotationPage, {
            backgroundColor: "#ffffff",
            scale: 2,
            useCORS: true,
            windowWidth: quotationPage.scrollWidth || 794,
          });

          const pdfDocument = new jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "a4",
          });
          const margin = 24;
          const pageWidth = pdfDocument.internal.pageSize.getWidth() - margin * 2;
          const pageHeight = pdfDocument.internal.pageSize.getHeight() - margin * 2;
          const imageHeight = (canvas.height * pageWidth) / canvas.width;
          let renderedHeight = 0;

          while (renderedHeight < imageHeight) {
            if (renderedHeight > 0) pdfDocument.addPage();

            pdfDocument.addImage(
              canvas,
              "PNG",
              margin,
              margin - renderedHeight,
              pageWidth,
              imageHeight
            );

            renderedHeight += pageHeight;
          }

          const pdfBlob = pdfDocument.output("blob");
          cleanup();
          resolve(pdfBlob);
        } catch (error) {
          cleanup();
          reject(error);
        }
      };

      document.body.appendChild(renderFrame);
      renderFrame.srcdoc = getQuotationHtml();
    });

  const handlePreview = async () => {
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) return;

    try {
      const pdfBlob = await generateQuotationPdfBlob();
      const pdfUrl = URL.createObjectURL(pdfBlob);
      previewWindow.location.href = pdfUrl;
      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (error) {
      previewWindow.close();
      console.error("Failed to preview quotation PDF:", error);
    }
  };

  const handleDownload = async () => {
    try {
      const pdfBlob = await generateQuotationPdfBlob();
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement("a");

      downloadLink.href = pdfUrl;
      downloadLink.download = getQuotationFileName();
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();

      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
    } catch (error) {
      console.error("Failed to download quotation PDF:", error);
    }
  };

  const isPreviewAction = action === "preview";

  return (
    <button
      type="button"
      onClick={isPreviewAction ? handlePreview : handleDownload}
      className={
        isPreviewAction
          ? "inline-flex items-center gap-1.5 rounded-md border border-[#BFD3FF] bg-white px-3 py-1.5 text-xs font-semibold text-[#175CD3] transition hover:bg-[#EFF6FF]"
          : "inline-flex items-center gap-1.5 rounded-md border border-[#A6E3C8] bg-white px-3 py-1.5 text-xs font-semibold text-[#078B5A] transition hover:bg-[#F0FDF4]"
      }
      title={`${isPreviewAction ? "View" : "Download"} R${quotationIndex} quotation PDF`}
    >
      {isPreviewAction ? (
        <svg aria-hidden="true" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
          <circle cx="12" cy="12" r="2.5" />
        </svg>
      ) : (
        <svg aria-hidden="true" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" />
        </svg>
      )}
      {isPreviewAction ? "View" : "Download"}
    </button>
  );
};

export default QuotationTrailPdfButton;
